import type { RouteLocationNormalized } from 'vue-router'
import cookieConfig from '@/config/cookie'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { getCookieDomain } from '@/utils/cookie'
import { CCOUNTRY, CHATBOTLOCALE, CLOCALE } from '@/utils/cookie/cookieTypes'
import type { CountryCodes, Locales } from '@/enums/general'
import { determineLocaleCountryPrefix, hasOnlyValidCountryParam } from '@/utils/parse'
import { stripLanguageRegex } from '@/utils/general'

interface LocaleCountryData {
  locale: string
  country: string
}

interface StoreDataParams {
  appStore: ReturnType<typeof useAppStore>
  data: LocaleCountryData
}

export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized) => {
  const appStore = useAppStore()
  const authStore = useAuthStore()

  // Get cookies
  const cookieLocale = useCookie(CLOCALE).value as string | undefined
  const cookieCountry = useCookie(CCOUNTRY).value as string | undefined

  // Get accept-language header and IP for server-side detection
  let acceptLanguageHeader: string | undefined
  let requestIp: string | undefined

  if (import.meta.server) {
    const headers = useRequestHeaders(['accept-language'])
    acceptLanguageHeader = headers['accept-language'] as string | undefined
    requestIp = authStore.ipAddress
    if (!requestIp) {
      try {
        const event = useRequestEvent()
        if (event) {
          const { getClientIp } = await import('@/utils/ip')
          console.log('getClientIp', getClientIp)
          requestIp = getClientIp(event) || undefined
        }
      } catch (_e) {
        // Ignore errors - requestIp will remain undefined
      }
    }
  }

  // Determine what the locale-country prefix should be
  const targetPrefix = await determineLocaleCountryPrefix({
    route: to,
    appStore,
    authStore,
    cookieLocale,
    cookieCountry,
    acceptLanguageHeader,
    requestIp
  })

  // Extract current prefix from path
  const pathSegments = to.path.split('/').filter(Boolean)
  const currentPrefix = pathSegments[0]

  // If current prefix doesn't match target prefix, redirect
  if (currentPrefix !== targetPrefix) {
    // Remove current prefix if it exists, then prepend target prefix
    // Use to.path instead of to.fullPath to avoid issues with query/hash
    let remainingPath = to.path

    // Ensure remainingPath ends with '/' if not already
    if (remainingPath !== '/' && !remainingPath.endsWith('/')) {
      remainingPath = `${remainingPath}/`
    }

    if (currentPrefix) {
      if (hasOnlyValidCountryParam({ appStore, localeCountryParam: currentPrefix })) {
        // Remove single country prefix (e.g., /nl -> /)
        // Use replace with limit to only replace first occurrence
        remainingPath = to.path.replace(new RegExp(`^/${currentPrefix}(/|$)`), '/') || '/'
      } else {
        // Remove the locale-country prefix (e.g., /en-nl -> /)
        // Use a regex that handles both with and without trailing slash
        const localeCountryRegex = new RegExp(stripLanguageRegex, 'i')
        remainingPath = to.path.replace(localeCountryRegex, '/') || '/'
      }
    }

    // Normalize: ensure remainingPath is '/' if empty, or starts with '/'
    if (!remainingPath || remainingPath === '') {
      remainingPath = '/'
    } else if (!remainingPath.startsWith('/')) {
      remainingPath = `/${remainingPath}`
    }
    // Prevent redirect loop: check if remainingPath already starts with targetPrefix
    // This can happen if the path was malformed (e.g., /en-nl/en-pt/...)
    if (remainingPath.startsWith(`/${targetPrefix}/`) || remainingPath === `/${targetPrefix}`) {
      // Already has the target prefix, don't redirect to avoid loop
      return
    }

    // Reconstruct full path with query and hash
    const queryString =
      to.query && Object.keys(to.query).length > 0 ? '?' + new URLSearchParams(to.query as Record<string, string>).toString() : ''
    const hash = to.hash || ''
    const newPath = `/${targetPrefix}${remainingPath === '/' ? '' : remainingPath}${queryString}${hash}`

    return navigateTo(newPath, { redirectCode: 301 })
  }

  // Prefix matches - extract locale and country and store them
  const [locale, country] = targetPrefix.split('-')
  const defaults: LocaleCountryData = {
    locale: cookieLocale || 'en',
    country: authStore.loggedIn
      ? authStore.user?.user?.country?.name?.toLowerCase() ?? appStore.defaultCountry
      : cookieCountry || appStore.defaultCountry
  }

  const data: LocaleCountryData = {
    locale: locale || defaults.locale,
    country: country || defaults.country
  }

  storeData({ appStore, data })
})

/**
 * Stores the locale and country data in the app store and cookies
 *
 * @param params - Object containing appStore and data with locale and country
 */
function storeData({ appStore, data: { locale, country } }: StoreDataParams): void {
  // Set store values
  appStore.setLanguage(locale as (typeof Locales)[keyof typeof Locales])
  appStore.setCountry(country as (typeof CountryCodes)[keyof typeof CountryCodes])

  // Set cookies with proper options - only update if different
  const cookieOptions = { path: '/', maxAge: cookieConfig.maxAge, domain: getCookieDomain() }
  const localeCookie = useCookie(CLOCALE, cookieOptions)
  const chatbotLocaleCookie = useCookie(CHATBOTLOCALE, cookieOptions)
  const countryCookie = useCookie(CCOUNTRY, cookieOptions)

  // Only update cookies if they're different from current values
  if (localeCookie.value !== locale) {
    localeCookie.value = locale
    chatbotLocaleCookie.value = locale
  }

  if (countryCookie.value !== country) {
    countryCookie.value = country
  }
}
